'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { DURATION, EASE_OUT_STRONG } from '@/lib/motion-utils';
import { PlaceholdersAndVanishInput } from '@/components/ui/placeholders-and-vanish-input';
import { SectionHeader } from '@/components/SectionHeader';
import { MailThemedIcon } from '@/components/icons';
import { useWebHaptics } from 'web-haptics/react';
import { useAnalytics } from '@/hooks/useAnalytics';

// Hoisted to module level to avoid recreation on each render
const PLACEHOLDERS = ['Enter email'];
export function Newsletter() {
  const $ = _c(15);
  const form = useNewsletterForm();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionHeader icon={MailThemedIcon} label="Join my mailing list" />;
    t1 = <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Get notified when I ship new projects and share thoughts on building.</p>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== form.handleAnimationComplete || $[3] !== form.handleSubmit || $[4] !== form.showSuccess || $[5] !== form.updateNewsletterEmail) {
    t2 = <PlaceholdersAndVanishInput placeholders={PLACEHOLDERS} onChange={form.updateNewsletterEmail} onSubmit={form.handleSubmit} isSuccess={form.showSuccess} onAnimationComplete={form.handleAnimationComplete} />;
    $[2] = form.handleAnimationComplete;
    $[3] = form.handleSubmit;
    $[4] = form.showSuccess;
    $[5] = form.updateNewsletterEmail;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== form.message || $[8] !== form.status) {
    t3 = form.status === "error" && <m.p initial={{
      opacity: 0,
      y: -4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -4,
      transition: {
        duration: DURATION.exit
      }
    }} transition={{
      duration: DURATION.enter,
      ease: EASE_OUT_STRONG
    }} className="mt-2 text-sm text-red-600 dark:text-red-400">{form.message}</m.p>;
    $[7] = form.message;
    $[8] = form.status;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <AnimatePresence>{t3}</AnimatePresence>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className="mt-8 rounded-2xl border border-zinc-100 p-6 dark:border-zinc-700/40">{t0}{t1}<div className="mt-6">{t2}{t4}</div></div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function useNewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [animationComplete, setAnimationComplete] = useState(false);
  const {
    track
  } = useAnalytics();
  const haptic = useWebHaptics();

  // Success UI shows only when both the API succeeded and the input's vanish
  // animation finished. Derived from existing state — no extra state/effect.
  const showSuccess = status === 'success' && animationComplete;
  const updateNewsletterEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Reset status when user types
    if (status !== 'idle' || showSuccess) {
      setStatus('idle');
      setMessage('');
      setAnimationComplete(false);
    }
  };
  const handleAnimationComplete = () => {
    setAnimationComplete(true);
  };
  const handleSubmit = async (e_0: React.FormEvent<HTMLFormElement>) => {
    e_0.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email
        })
      });
      if (!response.ok) {
        // The route reports why in `error`, but an upstream 502 returns HTML —
        // so the body is parsed defensively.
        const failure: {
          error?: string;
        } = await response.json().catch(() => ({}));
        track('newsletter_signup', {
          success: false,
          error: failure.error
        });
        haptic.trigger('error');
        setStatus('error');
        setMessage(failure.error || 'Something went wrong');
        return;
      }
      const data: {
        message?: string;
      } = await response.json().catch(() => ({}));
      track('newsletter_signup', {
        success: true
      });
      haptic.trigger('success');
      setStatus('success');
      setMessage(data.message || 'Successfully subscribed!');
      setEmail('');
      // Don't show success immediately, wait for animation
    } catch (error) {
      track('newsletter_signup', {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to connect'
      });
      haptic.trigger('error');
      setStatus('error');
      setMessage('Failed to connect. Please try again.');
    }
  };
  return {
    status,
    message,
    showSuccess,
    updateNewsletterEmail,
    handleAnimationComplete,
    handleSubmit
  };
}